/**
 * Printed-mech-state helpers — the pure, reference-free logic shared by the roster
 * reducer's repair/commit commands and the spawn/commit seam.
 *
 * The blueprint-derived **maxima** (maxHP, Repair Cap *max*, structure/stress) are NEVER
 * computed here — they live in `@lancer-vtt/rules` `compute`, and `@lancer-vtt/domain`
 * must never depend on `@lancer-vtt/rules`. So every function here takes the maxima *in*
 * (the caller derives them live and passes them); this keeps the reducer pure and the
 * raw→computed dependency one-way.
 */

import type { PrintedMechState } from "./types";

/**
 * The combatant-resource key that carries a deployed mech's **Repair Cap remaining**.
 * Repair Cap rides the existing generic per-combatant resource system
 * (`current.resources`, `resource_set`/`resource_delta`) rather than a new primitive —
 * `spawnMech` seeds `current.resources[REPAIR_CAP_RESOURCE]` and the commit extractor
 * reads it, so an in-combat spender gets writeback for free. The *max* is always
 * re-derived from `compute`; only the *current/remaining* lives in the resource.
 */
export const REPAIR_CAP_RESOURCE = "repair_cap";

/**
 * The combatant-resource key that carries a mech's **Eject lockout** — RAW p.70: Eject is
 * locked out "until you Full Repair". Presence (`>= 1`) means locked; absence means
 * usable, mirroring the sparse `resource_set`/`?? 0` reads every other resource in this
 * system uses (NOT the absent-means-loaded convention the Loading resource uses — this one
 * reads the ordinary way). Seeded onto a live combatant by `resolveEject`'s resource_set step and
 * re-seeded at `spawnMech` from the persisted {@link "./types" PrintedMechState.ejectSpent}
 * marker; cleared only by a Full Repair (which deletes the whole sparse printed-mech record,
 * this flag included) or a fresh deploy with no such marker.
 */
export const EJECT_LOCKOUT_RESOURCE = "eject_lockout";

/** Repairs spent to restore HP to full (Core p. 82): 1 Repair. */
export const REPAIR_COST_HP = 1;
/** Repairs spent to repair 1 Structure (Core p. 82): 2 Repairs. */
export const REPAIR_COST_STRUCTURE = 2;
/** Repairs spent to repair 1 Stress (Core p. 82): 2 Repairs. */
export const REPAIR_COST_STRESS = 2;
/**
 * Repairs spent to repair ONE destroyed weapon or system (Core p. 82: "1 REPAIR: Restore full
 * HP, or repair a destroyed weapon or system"). Same 1-Repair cost as the HP track; a distinct
 * constant because it's a distinct RAW line item, not a derivation off `REPAIR_COST_HP`.
 */
export const REPAIR_COST_ITEM = 1;
/**
 * Repairs spent to replenish 1 use of ALL Limited weapons/systems at once
 * (`rest_limited_charge_replenish` — t_grease_monkey:1's Unsanctioned Capacity Upgrade:
 * "…spend 2 Repairs to replenish 1 use of all Limited weapons and systems"). A
 * talent-granted option, not a Core p.82 base repair line item like the four constants
 * above — still rides the SAME generic `spend_repairs` track mechanism. The reducer
 * doesn't gate on talent ownership, mirroring every other track: the CLI/UI is the one
 * that only offers this option to a Grease Monkey pilot (UI mirrors rules, never
 * re-enforces them).
 */
export const REPAIR_COST_LIMITED = 2;

/**
 * Which consequence track a `spend_repairs` restores. `"item"` restores ONE destroyed
 * weapon/system named by the command's `itemKey`, distinct from the three numeric tracks
 * above — see {@link "./commands".SpendRepairsCommand.itemKey}. `"limited"` replenishes 1
 * charge on EVERY currently-consumed Limited weapon AND Limited system at once (no `itemKey`
 * — RAW's "all Limited weapons and systems" is a blanket spend, not a per-item pick).
 */
export type RepairTrack = "hp" | "structure" | "stress" | "item" | "limited";

/**
 * The Repairs cost of restoring a given track (Core p. 82). `structureRepairCostOverride`
 * (the Everest's "Replaceable parts" frame trait: "1 Repair per 1 structure damage, instead
 * of 2") overrides the `structure` track's flat `REPAIR_COST_STRUCTURE` when supplied — the
 * caller (`@lancer-vtt/rules`' `printedMechMaxes`, which resolves the frame and computes
 * `ComputedMechStats.structureRepairCost`) is the ONE place that knows a mech's frame; this
 * pure domain layer stays reference-free and just takes the resolved number in, mirroring how
 * `SpendRepairsCommand.maxStructure`/etc. are caller-computed rather than derived here.
 * Absent (the common case — every non-Everest frame) falls back to the flat default.
 */
export function repairCost(track: RepairTrack, structureRepairCostOverride?: number): number {
  switch (track) {
    case "hp":
      return REPAIR_COST_HP;
    case "structure":
      return structureRepairCostOverride ?? REPAIR_COST_STRUCTURE;
    case "stress":
      return REPAIR_COST_STRESS;
    case "item":
      return REPAIR_COST_ITEM;
    case "limited":
      return REPAIR_COST_LIMITED;
  }
}

/**
 * The ledger slot a frame's **Core Power** rides (Core p.32, p.263: a mech has 1 CP, spent by its
 * core system's activation and regained only on a Full Repair or as a GM boon). Every frame's core
 * activation freezes a Limited pool `{max: 1, pool: CORE_POWER_POOL}` (`CORE_POWER_LIMITED`,
 * `packages/rules/src/encounter/spawn/effectFreeze.ts`), so the spent CP persists
 * through `PrintedMechState.limitedSystemUsed` like a Limited system's charge. Unlike one, it is
 * skipped by {@link replenishLimitedUsed}: RAW's 2-Repair rest spend replenishes Limited weapons and
 * systems, not Core Power.
 */
export const CORE_POWER_POOL = "core_power";

/**
 * Replenish 1 use of EVERY currently-consumed Limited charge in ONE bag
 * (`rest_limited_charge_replenish` — the `spend_repairs` `track: "limited"` apply
 * step). Decrements each entry by exactly 1 (never below 0) and drops any entry that reaches
 * 0 — sparse, mirroring every other field here ("absent" always means "full"/untouched). A
 * weapon/system with more than 1 charge spent stays partly consumed after one replenish
 * (RAW: "1 use", not "reset to full"); a `Limited 1` item that had its lone charge spent is
 * fully restored by the same single spend. `undefined`/empty input (nothing consumed)
 * returns `undefined` — the caller ({@link "./reducer".applySpendRepairs}) hard-rejects that
 * case before ever calling this (a `track: "limited"` spend with nothing to replenish is a
 * wasted spend, refused the same way `track: "item"` refuses a non-destroyed `itemKey`), so
 * this function itself stays a pure, unconditional decrement — no legality check duplicated
 * here.
 *
 * **Reused verbatim for BOTH bags** (`limited_system_charge_namespace`):
 * `applySpendRepairs` calls this once for
 * `PrintedMechState.limitedUsed` (weapons) and once for `.limitedSystemUsed` (systems) on
 * the SAME `track: "limited"` spend — RAW's "replenish 1 use of all Limited weapons AND
 * systems" is one 2-Repair spend touching both bags at once, not two separate spends, so the
 * two calls share the single Repair cost, never charged twice.
 */
export function replenishLimitedUsed(limitedUsed: Record<string, number> | undefined): Record<string, number> | undefined {
  if (!limitedUsed) return undefined;
  const out: Record<string, number> = {};
  for (const [key, count] of Object.entries(limitedUsed)) {
    // Core Power is not a Limited system: only a Full Repair (or a GM boon) restores it (Core p.263).
    if (key === CORE_POWER_POOL) {
      out[key] = count;
      continue;
    }
    const next = count - 1;
    if (next > 0) out[key] = next;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** The blueprint-derived maxima a sparse {@link PrintedMechState} is measured against. */
export type PrintedMechMaxes = {
  /** Max HP (`compute`'s `hp`). */
  maxHp: number;
  /** Max structure (frame stat; player mechs 4). */
  maxStructure: number;
  /** Max stress (frame stat; player mechs 4). */
  maxStress: number;
  /** Repair Cap max (`compute`'s `repcap`). */
  repairCapMax: number;
  /**
   * Repairs cost to restore 1 Structure — `compute`'s
   * `ComputedMechStats.structureRepairCost`, threaded through to {@link repairCost}'s override
   * param. Optional (unlike the maxima above) because `resolvePrintedMechValues`/
   * `sparsePrintedMechState`'s other callers (`spawn.ts`) have no cost to supply — only
   * `@lancer-vtt/rules`' `printedMechMaxes` (which resolves the frame) populates it; absent
   * defaults to the flat `REPAIR_COST_STRUCTURE` at {@link repairCost}'s call site.
   */
  structureRepairCost?: number;
}

/** The full (non-sparse) current values of a printed mech's carried tracks. */
export type PrintedMechValues = {
  hp: number;
  structure: number;
  stress: number;
  repairsRemaining: number;
}

/**
 * Resolve a (possibly absent) sparse {@link PrintedMechState} to its full current values,
 * defaulting each absent field to its pristine max (absent == pristine).
 */
export function resolvePrintedMechValues(
  printed: PrintedMechState | undefined,
  maxes: PrintedMechMaxes,
): PrintedMechValues {
  return {
    hp: printed?.hp ?? maxes.maxHp,
    structure: printed?.structure ?? maxes.maxStructure,
    stress: printed?.stress ?? maxes.maxStress,
    repairsRemaining: printed?.repairsRemaining ?? maxes.repairCapMax,
  };
}

/**
 * Build a **sparse** {@link PrintedMechState} from full current values: drop every field
 * that equals its pristine max (absent == pristine), and return `null` when *every* field
 * is pristine (so the caller deletes the `printedMechs` key entirely — the mech is fully
 * repaired). The single source of the "what counts as diverged" rule, shared by
 * `spend_repairs` and the commit extractor so the stored shape can't drift.
 */
export function sparsePrintedMechState(
  values: PrintedMechValues,
  maxes: PrintedMechMaxes,
): PrintedMechState | null {
  const out: PrintedMechState = {};
  if (values.hp < maxes.maxHp) out.hp = values.hp;
  if (values.structure < maxes.maxStructure) out.structure = values.structure;
  if (values.stress < maxes.maxStress) out.stress = values.stress;
  if (values.repairsRemaining < maxes.repairCapMax) out.repairsRemaining = values.repairsRemaining;
  return Object.keys(out).length === 0 ? null : out;
}
